/*
 * Sticky add-to-cart bar on mobile, 2026-10-02. Markup: snippets/sticky-atc.liquid.
 * Shows when the main add-to-cart button is scrolled above the viewport. Its
 * button submits the main product form, so product-form.js does the AJAX add
 * and opens the cart drawer. Price and variant line follow variantChange.
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        const json = this.querySelector('[data-sticky-atc-data]');
        this.data = json ? JSON.parse(json.textContent) : { frameIndex: -1, sizeIndex: -1, options: [] };
        this.sectionId = this.dataset.section;
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.priceEl = this.querySelector('[data-sticky-atc-price]');
        this.variantEl = this.querySelector('[data-sticky-atc-variant]');
        this.button = this.querySelector('[data-sticky-atc-button]');
        this.buttonText = this.querySelector('[data-sticky-atc-button-text]');
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        this.variantEl.textContent = this.variantLine(this.data.options, this.data.title);
        this.button.addEventListener('click', this.onClick.bind(this));

        if (!this.mainButton) return;
        this.syncButton();
        this.buttonObserver = new MutationObserver(this.syncButton.bind(this));
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          characterData: true,
          subtree: true,
        });

        // A scroll check, not only an IntersectionObserver: a fast jump past the
        // button (fling, anchor link) never makes it intersect, so no IO entry fires.
        this.onScroll = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            const rect = this.mainButton.getBoundingClientRect();
            // 40px of slack before hiding again, so a small layout jump right at
            // the edge cannot show and hide the bar in quick succession.
            const visible = this.isVisible ? rect.bottom < 40 : rect.bottom < 0;
            if (visible !== this.isVisible) this.toggle(visible);
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();

        this.onMediaChange = () => this.toggle(this.isVisible);
        this.mobileQuery.addEventListener('change', this.onMediaChange);

        if (typeof subscribe === 'function') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.sectionId || !data.variant) return;
            this.updatePrice();
            this.variantEl.textContent = this.variantLine(data.variant.options, data.variant.title);
          });
        }
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        this.buttonObserver?.disconnect();
        this.unsubscribe?.();
        this.mobileQuery?.removeEventListener('change', this.onMediaChange);
        document.body.classList.remove('sticky-atc-open');
      }

      toggle(visible) {
        const show = Boolean(visible);
        this.isVisible = show;
        this.classList.toggle('sticky-atc--visible', show);
        this.toggleAttribute('inert', !show);
        const open = show && this.mobileQuery.matches;
        if (open) document.body.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
        document.body.classList.toggle('sticky-atc-open', open);
      }

      onClick() {
        if (this.button.disabled || this.mainButton?.getAttribute('aria-disabled') === 'true') return;
        const form = document.getElementById(this.dataset.formId);
        if (form && typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          this.mainButton?.click();
        }
      }

      syncButton() {
        this.button.disabled = this.mainButton.hasAttribute('disabled');
        const busy = this.mainButton.getAttribute('aria-disabled') === 'true';
        this.button.toggleAttribute('aria-disabled', busy);
        const text = this.mainButton.querySelector('span')?.textContent.trim();
        if (text) this.buttonText.textContent = text;
      }

      updatePrice() {
        const price = document.getElementById(`price-${this.sectionId}`);
        if (!price) return;
        const item = price.querySelector('.price--on-sale')
          ? price.querySelector('.price__sale .price-item--last')
          : price.querySelector('.price__regular .price-item--regular');
        const text = (item || Array.from(price.querySelectorAll('.price-item')).pop())?.textContent.trim();
        if (text) this.priceEl.textContent = text;
      }

      variantLine(options, title) {
        const frame = options?.[this.data.frameIndex];
        const size = options?.[this.data.sizeIndex];
        if (!frame) return title && title !== 'Default Title' ? title : '';

        const parts = [];
        let frameNote = '';
        if (frame.includes('Paper')) {
          parts.push('Archival Paper');
        } else if (frame.includes('Gallery Wrapped Canvas')) {
          parts.push('Premium Canvas');
          frameNote = 'No Frame';
        } else {
          const color = frame.match(/Framed Canvas \(([^)]+)\)/);
          parts.push('Premium Canvas');
          frameNote = color ? `${color[1]} frame` : frame;
        }
        if (size) parts.push(size);
        if (frameNote) parts.push(frameNote);
        return parts.join(' · ');
      }
    }
  );
}
